import { CancelButton, SaveButton } from '@core/components/Buttons';
import { FormDialogContent, SimpleDialog } from '@core/components/Dialog';
import { TextLabel } from '@core/components/Inputs';
import { useReduxLoading } from '@core/hooks';
import { useApiUtils } from '@core/util/apiUtils';
import { Grid } from '@material-ui/core';
import InputMonetario from 'app/components/Input/InputMonetario';
import { cancelarAlert } from 'app/services/alerts';
import conciliacaoApi from 'app/services/api/conciliacaoApi';
import * as Actions from 'app/store/actions';
import { getCurrency } from 'app/utils/monetarioUtil';
import { useFormik } from 'formik';
import React, { useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import makeConciliacao from '../../MakeConciliacao';
import { ConciliacaoContext } from '../ConciliacaoContext';
import { ConciliacaoModal } from './ConciliacaoModal';

const AcrescimoDescontoModal = ({ filtros }) => {
  const [conciliacoes, setConciliacoes] = useContext(ConciliacaoContext).conciliacoes;
  const [searchConciliacoes, setSearchConciliacoes] = useContext(ConciliacaoContext).searchConciliacoes;
  const [modal, setModal] = useContext(ConciliacaoContext).modal;
  const [acrescimoDesconto] = useContext(ConciliacaoContext).acrescimoDesconto;
  const [extratoSelecionado] = useContext(ConciliacaoContext).extratoSelecionado;
  const [loading, setLoading] = useState(false);
  const [credito, setCredito] = useContext(ConciliacaoContext).credito;
  const [debito, setDebito] = useContext(ConciliacaoContext).debito;
  const [saldoFinal, setSaldoFinal] = useContext(ConciliacaoContext).saldoFinal;
  const [conciliacoesPendentes, setConciliacoesPendentes] = useContext(ConciliacaoContext).conciliacoesPendentes;
  const [valorDiferenca, setValorDiferenca] = useState(0);
  const dispatch = useDispatch();

  const apiUtils = useApiUtils(dispatch, Actions);

  useReduxLoading(loading);

  const close = () => {
    formik.resetForm({});
    setModal('');
  };

  const showSuccess = (message) => {
    dispatch(Actions.showMessage({ message, variant: 'success' }));
  };

  const showError = (message) => {
    dispatch(Actions.showMessage({ message, variant: 'error' }));
  };

  const handleCloseModal = () => {
    if (formik.dirty) {
      cancelarAlert({
        confirmar: close,
      });
    } else {
      close();
    }
  };

  const getInitialValues = (registro) => ({
    ...registro,
    id: registro?.id,
    juros: registro?.juros,
    multa: registro?.multa,
    desconto: registro?.desconto,
  });

  const handleSubmit = async () => {
    setLoading(true);

    let request = {};

    const juros = formik.values.juros || 0;
    const multa = formik.values.multa || 0;
    const desconto = formik.values.desconto || 0;

    if (extratoSelecionado.contaReceberList) {
      request = {
        juros: juros,
        multa: multa,
        desconto: desconto,
        lancamentoExtrato: extratoSelecionado?.lancamentoExtrato,
        conciliacaoRequest: {
          id: extratoSelecionado.extratoId,
          contaReceberList: makeConciliacao.makeConciliacoesContaReceber(extratoSelecionado),
          contaPagarList: makeConciliacao.makeConciliacoesContaPagar(extratoSelecionado),
        },
      };
      if (!request.conciliacaoRequest.contaPagarList || request.conciliacaoRequest.contaPagarList.length === 0) {
        delete request.conciliacaoRequest.contaPagarList;
      }
    } else {
      request = {
        juros: juros,
        multa: multa,
        desconto: desconto,
        lancamentoExtrato: extratoSelecionado?.lancamentoExtrato,
        conciliacaoRequest: {
          id: extratoSelecionado.extratoId,
          contaPagarList: makeConciliacao.makeConciliacoesContaPagar(extratoSelecionado),
        },
      };
    }

    if (valorDiferenca === parseFloat(0).toFixed(2)) {
      try {
        let conciliacaoFilter = conciliacoes;
        conciliacaoFilter = conciliacaoFilter.filter((conciliacao) => conciliacao.id !== request.conciliacaoRequest.id);
        setConciliacoes([]);
        const resp = await conciliacaoApi.acrescimosDescontos(request);

        if (resp.status === 200) {
          setConciliacoes(conciliacaoFilter);
          atualizarDashboard(resp.data[0]);
          showSuccess('Conciliação realizada com sucesso.');

          formik.resetForm({});
          setModal('');
        }
      } catch (err) {
        setLoading(false);
        apiUtils.handleApiError(err);
        close();
      } finally {
        setLoading(true);
        const resp = await conciliacaoApi.buscarLancamentos(filtros, searchConciliacoes);
        if (resp) {
          setSearchConciliacoes({
            size: resp.data.lancamentoExtratoBancarioResponse.size,
            page: resp.data.lancamentoExtratoBancarioResponse.number,
            totalElements: resp.data.lancamentoExtratoBancarioResponse.totalElements,
            sort: searchConciliacoes?.sort,
          });

          setConciliacoes(resp.data.lancamentoExtratoBancarioResponse.content);
        }
        setLoading(false);
      }
    } else {
      showError('O valor da diferença deve ser igual a zero para conciliar.');
    }

    setLoading(false);
  };

  const atualizarDashboard = (conciliacao) => {
    if (conciliacao.valor > 0) {
      setCredito(credito - Math.abs(conciliacao.valor));
    } else {
      setDebito(debito - Math.abs(conciliacao.valor));
    }

    setSaldoFinal(Math.abs(saldoFinal - Math.abs(conciliacao.valor)));
    setConciliacoesPendentes(conciliacoesPendentes - 1);
  };

  let formik = useFormik({
    initialValues: getInitialValues(acrescimoDesconto),
    onSubmit: handleSubmit,
    validateOnChange: true,
    validateOnBlur: true,
  });

  useEffect(() => {
    const juros = formik.values.juros || 0;
    const multa = formik.values.multa || 0;
    const desconto = formik.values.desconto || 0;
    const valorDiferencaExtrato = extratoSelecionado?.valorDiferenca?.toFixed(2);

    if (extratoSelecionado?.valorReceitaDespesa < extratoSelecionado?.valorExtrato) {
      setValorDiferenca((valorDiferencaExtrato - juros - multa + desconto).toFixed(2));
    } else {
      setValorDiferenca((valorDiferencaExtrato + juros + multa - desconto).toFixed(2));
    }
    // Efeito reativo apenas ao gatilho listado; incluir as demais dependências alteraria
    // quando o efeito dispara.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values]);

  return (
    <>
      <SimpleDialog open={modal === ConciliacaoModal.acrecimoDesconto} onClose={handleCloseModal}>
        <FormDialogContent
          onSubmit={formik.handleSubmit}
          header={{
            title: 'Acréscimos/Descontos',
            toolbar: (
              <>
                <CancelButton title="cancelar" type="button" onClick={handleCloseModal}>
                  Cancelar
                </CancelButton>
                <SaveButton color="buttonSave" type="submit">
                  Salvar
                </SaveButton>
              </>
            ),
          }}
          content={
            <div className="h-full max-h-full w-full">
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <InputMonetario
                    id="juros"
                    name="juros"
                    handleChange={formik.handleChange}
                    maximumValue="999999999.99"
                    onBlur={formik.handleBlur}
                    value={formik.values.juros}
                    fullWidth
                    autoFocus
                    disabled={extratoSelecionado?.valorReceitaDespesa > extratoSelecionado?.valorExtrato}
                    label="Juros"
                  />
                </Grid>
                <Grid item xs={12}>
                  <InputMonetario
                    id="multa"
                    name="multa"
                    handleChange={formik.handleChange}
                    maximumValue="999999999.99"
                    onBlur={formik.handleBlur}
                    value={formik.values.multa}
                    fullWidth
                    disabled={extratoSelecionado?.valorReceitaDespesa > extratoSelecionado?.valorExtrato}
                    label="Multa"
                  />
                </Grid>
                <Grid item xs={12}>
                  <InputMonetario
                    id="desconto"
                    name="desconto"
                    handleChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.desconto}
                    maximumValue="999999999.99"
                    fullWidth
                    disabled={extratoSelecionado?.valorReceitaDespesa < extratoSelecionado?.valorExtrato}
                    label="Desconto"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextLabel
                    id="valorExtrato"
                    label="Valor do extrato (R$)"
                    value={getCurrency(extratoSelecionado?.valorExtrato)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextLabel
                    id="valorReceitaDespesa"
                    label={
                      extratoSelecionado?.tipo === 'RECEITA'
                        ? 'Valor da receita selecionada (R$)'
                        : 'Valor da despesa selecionada (R$)'
                    }
                    value={getCurrency(extratoSelecionado?.valorReceitaDespesa)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextLabel
                    id="valorDiferenca"
                    label="Valor da diferença (R$)"
                    value={
                      formik.values.juros === undefined &&
                      formik.values.multa === undefined &&
                      formik.values.desconto === undefined
                        ? getCurrency(extratoSelecionado?.valorDiferenca)
                        : getCurrency(valorDiferenca)
                    }
                  />
                </Grid>
              </Grid>
            </div>
          }
        />
      </SimpleDialog>
    </>
  );
};

export default AcrescimoDescontoModal;
